/**
 * useMedicationSafetyChecks
 *
 * Phase 2 medication safety: duplicate therapy detection and formulary compliance.
 * Queries active FhirMedicationStatements (active med list) and FhirMedicationKnowledges
 * (therapeutic class + formulary status) and produces ClinicalAlerts for display in
 * AdvancedClinicalSafetyMonitor.
 *
 * Backend enforcement (rl-4-2-004, rl-4-2-005) runs independently via Zen Engine at
 * order entry. This hook surfaces display-level alerts for the safety monitor widget.
 *
 * DRR-114: FhirMedicationStatement = T2 (1yr). FhirMedicationKnowledge = T2 (1yr).
 * DRR-149: Enrichment pipeline pre-computes formulary/classification server-side;
 *          this hook parses the stored FHIR fields for frontend display only.
 */

import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useMemo } from 'react';
import type { ClinicalAlert } from './AdvancedClinicalSafetyMonitor';

// ─────────────────────────────────────────────────────────────────────────────
// GraphQL documents (inline — SDL types not yet in generated/graphql.ts)
// ─────────────────────────────────────────────────────────────────────────────

const MEDICATION_STATEMENTS_QUERY = gql`
  query GetActiveMedicationStatements($tenantId: ID!) {
    fhirMedicationStatements(tenantId: $tenantId, limit: 100) {
      id
      status
      subject
      medicationCodeableConcept
    }
  }
`;

const MEDICATION_KNOWLEDGE_QUERY = gql`
  query GetMedicationKnowledge($tenantId: ID!) {
    fhirMedicationKnowledges(tenantId: $tenantId, limit: 100) {
      id
      code
      medicineClassification
      regulatory
    }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Raw FHIR response shapes
// ─────────────────────────────────────────────────────────────────────────────

interface RawMedicationStatement {
  id: string;
  status?: string | null;
  subject?: string | null;
  medicationCodeableConcept?: string | null;
}

interface RawMedicationKnowledge {
  id: string;
  code?: string | null;
  medicineClassification?: string | null;
  regulatory?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// JSON parsers for serialized FHIR fields
// ─────────────────────────────────────────────────────────────────────────────

interface ParsedCodeableConcept {
  text?: string;
  coding?: Array<{ system?: string; code?: string; display?: string }>;
}

interface ParsedMedicineClassification {
  type?: { text?: string };
  classification?: Array<{
    text?: string;
    coding?: Array<{ code?: string; display?: string }>;
  }>;
}

// FERP extension to FHIR R4 MedicationKnowledge.regulatory:
// the enrichment pipeline (DRR-149) stores formularyStatus alongside standard FHIR fields.
interface ParsedRegulatory {
  regulatoryAuthority?: { reference?: string };
  substitution?: Array<{ type?: { text?: string }; allowed?: boolean }>;
  schedule?: Array<{ schedule?: { text?: string } }>;
  // DRR-149 enrichment output
  formularyStatus?: string;
}

function safeParse<T>(json: string | null | undefined): T | null {
  if (!json) return null;
  try {
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}

function getMedName(concept: ParsedCodeableConcept | null): string {
  return (
    concept?.text ??
    concept?.coding?.[0]?.display ??
    concept?.coding?.[0]?.code ??
    'Unknown Medication'
  );
}

function getTherapeuticClass(
  classification: ParsedMedicineClassification | null,
): string | null {
  return (
    classification?.classification?.[0]?.text ??
    classification?.classification?.[0]?.coding?.[0]?.display ??
    classification?.type?.text ??
    null
  );
}

function formularyStatusToSeverity(
  status: string,
): ClinicalAlert['severity'] | null {
  switch (status) {
    case 'REQUIRES_PRIOR_AUTHORIZATION':
    case 'NON_FORMULARY':
      return 'HIGH';
    case 'THERAPEUTIC_SUBSTITUTION_AVAILABLE':
    case 'ON_FORMULARY_WITH_RESTRICTION':
      return 'MEDIUM';
    case 'NOT_EVALUATED':
      return 'LOW';
    case 'ON_FORMULARY':
      return null; // No alert needed
    default:
      return null;
  }
}

function formularyAlertText(
  status: string,
  medName: string,
): { description: string; recommendation: string } {
  switch (status) {
    case 'REQUIRES_PRIOR_AUTHORIZATION':
      return {
        description: `${medName} requires prior authorization before dispensing.`,
        recommendation: 'Contact payer for prior authorization. Do not dispense until approved.',
      };
    case 'NON_FORMULARY':
      return {
        description: `${medName} is not on the current formulary.`,
        recommendation: 'Document clinical justification. Consider formulary alternative if available.',
      };
    case 'THERAPEUTIC_SUBSTITUTION_AVAILABLE':
      return {
        description: `A formulary-preferred equivalent is available for ${medName}.`,
        recommendation: 'Consider switching to a formulary alternative for better coverage.',
      };
    case 'ON_FORMULARY_WITH_RESTRICTION':
      return {
        description: `${medName} is covered with restrictions (step therapy or quantity limit may apply).`,
        recommendation: 'Verify restriction requirements before dispensing.',
      };
    case 'NOT_EVALUATED':
      return {
        description: `No formulary record found for ${medName}.`,
        recommendation: 'Add medication to FhirMedicationKnowledge for future formulary checks.',
      };
    default:
      return {
        description: `Formulary status for ${medName}: ${status}`,
        recommendation: 'Review formulary status before dispensing.',
      };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export interface MedicationSafetyCheckResult {
  duplicateTherapyAlerts: ClinicalAlert[];
  formularyAlerts: ClinicalAlert[];
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

/**
 * Queries active medication statements and medication knowledge records for a patient,
 * producing duplicate-therapy and formulary compliance alerts for the safety monitor.
 *
 * @param tenantId        - FERP tenant ID (required)
 * @param patientActorId  - FERP sequential actor ID (optional; returns empty arrays when absent)
 */
export function useMedicationSafetyChecks(
  tenantId: string,
  patientActorId: string | null | undefined,
): MedicationSafetyCheckResult {
  const skip = !tenantId || !patientActorId;

  const {
    data: statementsData,
    loading: statementsLoading,
    error: statementsError,
    refetch: refetchStatements,
  } = useQuery<{ fhirMedicationStatements: RawMedicationStatement[] }>(MEDICATION_STATEMENTS_QUERY, {
    variables: { tenantId },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: knowledgeData,
    loading: knowledgeLoading,
    error: knowledgeError,
    refetch: refetchKnowledge,
  } = useQuery<{ fhirMedicationKnowledges: RawMedicationKnowledge[] }>(MEDICATION_KNOWLEDGE_QUERY, {
    variables: { tenantId },
    skip,
    fetchPolicy: 'cache-and-network',
  });

  // Build a medication name → knowledge record lookup for class detection.
  const knowledgeByName = useMemo<Map<string, RawMedicationKnowledge>>(() => {
    const raw: RawMedicationKnowledge[] = knowledgeData?.fhirMedicationKnowledges ?? [];
    const map = new Map<string, RawMedicationKnowledge>();
    for (const k of raw) {
      const concept = safeParse<ParsedCodeableConcept>(k.code);
      const name = getMedName(concept).toLowerCase();
      map.set(name, k);
    }
    return map;
  }, [knowledgeData]);

  // Duplicate therapy: group active meds by therapeutic class; flag classes with 2+ drugs.
  const duplicateTherapyAlerts = useMemo<ClinicalAlert[]>(() => {
    const raw: RawMedicationStatement[] = statementsData?.fhirMedicationStatements ?? [];
    const activeMeds = raw.filter(
      s => (s.status ?? '').toLowerCase() === 'active',
    );

    const classToDrugs = new Map<string, string[]>();
    for (const med of activeMeds) {
      const concept = safeParse<ParsedCodeableConcept>(med.medicationCodeableConcept);
      const medName = getMedName(concept);
      const knowledge = knowledgeByName.get(medName.toLowerCase());
      const classification = safeParse<ParsedMedicineClassification>(
        knowledge?.medicineClassification ?? null,
      );
      const therapeuticClass = getTherapeuticClass(classification);
      if (therapeuticClass) {
        const bucket = classToDrugs.get(therapeuticClass) ?? [];
        bucket.push(medName);
        classToDrugs.set(therapeuticClass, bucket);
      }
    }

    const alerts: ClinicalAlert[] = [];
    for (const [drugClass, drugs] of classToDrugs.entries()) {
      if (drugs.length >= 2) {
        alerts.push({
          id: `duplicate-therapy-${drugClass.replace(/\s+/g, '-').toLowerCase()}`,
          type: 'DUPLICATE_THERAPY',
          severity: 'MEDIUM',
          title: `Therapeutic Duplication: ${drugClass}`,
          description: `Patient has ${drugs.length} concurrent medications in the same therapeutic class (${drugClass}): ${drugs.join(', ')}.`,
          recommendation: 'Pharmacist review required. Confirm therapeutic duplication is intentional or select an alternative.',
          triggeredAt: Date.now(),
          status: 'ACTIVE',
          evidence: [
            {
              type: 'MEDICATION_DATA',
              value: drugs.join(' + '),
              timestamp: Date.now(),
              source: 'FhirMedicationStatement',
            },
          ],
          interventionRequired: false,
        });
      }
    }
    return alerts;
  }, [statementsData, knowledgeByName]);

  // Formulary compliance: alerts for any med with a non-approved formulary status.
  const formularyAlerts = useMemo<ClinicalAlert[]>(() => {
    const raw: RawMedicationKnowledge[] = knowledgeData?.fhirMedicationKnowledges ?? [];
    const alerts: ClinicalAlert[] = [];

    for (const knowledge of raw) {
      const regulatory = safeParse<ParsedRegulatory>(knowledge.regulatory);
      if (!regulatory?.formularyStatus) continue;

      const severity = formularyStatusToSeverity(regulatory.formularyStatus);
      if (!severity) continue;

      const concept = safeParse<ParsedCodeableConcept>(knowledge.code);
      const medName = getMedName(concept);
      const { description, recommendation } = formularyAlertText(
        regulatory.formularyStatus,
        medName,
      );
      const statusLabel = regulatory.formularyStatus.replace(/_/g, ' ');

      alerts.push({
        id: `formulary-${knowledge.id}`,
        type: 'FORMULARY',
        severity,
        title: `Formulary: ${medName} — ${statusLabel}`,
        description,
        recommendation,
        triggeredAt: Date.now(),
        status: 'ACTIVE',
        evidence: [
          {
            type: 'MEDICATION_DATA',
            value: regulatory.formularyStatus,
            timestamp: Date.now(),
            source: 'FhirMedicationKnowledge.regulatory',
          },
        ],
        interventionRequired:
          regulatory.formularyStatus === 'REQUIRES_PRIOR_AUTHORIZATION' ||
          regulatory.formularyStatus === 'NON_FORMULARY',
      });
    }
    return alerts;
  }, [knowledgeData]);

  function refetch() {
    refetchStatements();
    refetchKnowledge();
  }

  return {
    duplicateTherapyAlerts,
    formularyAlerts,
    loading: statementsLoading || knowledgeLoading,
    error: statementsError ?? knowledgeError,
    refetch,
  };
}
